/**
 * Thumbnails - Left (stacked) gallery behaviour. Desktop only.
 *
 * The layout renders every media stacked vertically (no fader). This script:
 *   1. Click a thumbnail  -> smooth-scroll the page to that media.
 *   2. Scroll the images  -> highlight the thumbnail of the media in view (scroll-spy).
 *
 * Highlighting is delegated to the existing <product-thumbs> element: it observes
 * the `data-active-media` attribute on <product-images>, so we just set that.
 * Thumb clicks are intercepted in the capture phase to stop <product-thumbs> from
 * firing the fader's `theme:media:select` (which this layout does not use).
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 750px)';
  var SPY_MUTE_MS = 800; // ignore scroll-spy right after a click-scroll

  function headerOffset() {
    var px = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return (parseInt(px, 10) || 0) + 20;
  }

  function StackedThumbs(wrapper) {
    this.wrapper = wrapper;
    this.images = wrapper.querySelector('product-images');
    this.mediaList = wrapper.querySelector('[data-product-media-list]');
    this.slides = Array.prototype.slice.call(wrapper.querySelectorAll('[data-slide]'));
    this.mq = window.matchMedia(DESKTOP);
    this.muteSpyUntil = 0;
    this.enabled = false;

    this.onClick = this.onClick.bind(this);
    this.onSpy = this.onSpy.bind(this);
    this.sync = this.sync.bind(this);

    if (!this.images || !this.mediaList || this.slides.length < 2) return;
    this.mq.addEventListener('change', this.sync);
    this.sync();
  }

  StackedThumbs.prototype.sync = function () {
    if (this.mq.matches) this.enable();
    else this.disable();
  };

  StackedThumbs.prototype.enable = function () {
    if (this.enabled) return;
    this.enabled = true;
    // Capture phase: run before <product-thumbs>' own click handler and cancel it.
    this.images.addEventListener('click', this.onClick, true);
    this.images.addEventListener('keyup', this.onClick, true);
    this.io = new IntersectionObserver(this.onSpy, {
      rootMargin: '-' + headerOffset() + 'px 0px -55% 0px',
      threshold: 0,
    });
    this.slides.forEach(function (s) { this.io.observe(s); }, this);
  };

  StackedThumbs.prototype.disable = function () {
    if (!this.enabled) return;
    this.enabled = false;
    this.images.removeEventListener('click', this.onClick, true);
    this.images.removeEventListener('keyup', this.onClick, true);
    if (this.io) { this.io.disconnect(); this.io = null; }
  };

  StackedThumbs.prototype.onClick = function (e) {
    if (e.type === 'keyup' && e.code !== 'Enter' && e.code !== 'NumpadEnter') return;
    var link = e.target.closest && e.target.closest('[data-thumb-link]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation(); // keep the fader's media:select from firing
    var id = link.getAttribute('data-media-id');
    this.muteSpyUntil = performance.now() + SPY_MUTE_MS;
    this.activate(id);
    this.scrollToMedia(id);
  };

  StackedThumbs.prototype.onSpy = function (entries) {
    if (performance.now() < this.muteSpyUntil) return;
    var top = null;
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      if (!top || en.boundingClientRect.top < top.boundingClientRect.top) top = en;
    });
    if (top) this.activate(top.target.getAttribute('data-media-id'));
  };

  StackedThumbs.prototype.activate = function (id) {
    if (id) this.images.setAttribute('data-active-media', id);
  };

  StackedThumbs.prototype.scrollToMedia = function (id) {
    var media = this.mediaList.querySelector('[data-media-id="' + id + '"]');
    if (!media) return;
    var top = media.getBoundingClientRect().top;
    // Reuse the theme's own scroll util (sticky-header aware) — the same one
    // product-info uses to scroll to a media on variant change.
    if (window.theme && typeof window.theme.scrollTo === 'function') {
      window.theme.scrollTo(top);
    } else {
      window.scrollTo({ top: top + window.scrollY - headerOffset(), behavior: 'smooth' });
    }
  };

  function init() {
    document.querySelectorAll('.product__wrapper--thumbnails-left').forEach(function (w) {
      if (!w.__stackedThumbs) w.__stackedThumbs = new StackedThumbs(w);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
